import React from 'react';
import styled from 'styled-components';
import { AppDialog } from '../shared/AppDialog';
import { EnvelopeIcon } from '../shared/Icons';

export interface OtpFieldEntry {
  fieldName: string;
  label: string;
  contact: string;
}

interface OtpVerificationDialogProps {
  open: boolean;
  fields: OtpFieldEntry[];
  codes: Record<string, string[]>;
  error: string;
  onCodeChange: (fieldName: string, digitIndex: number, value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

const FieldBlock = styled.div`
  & + & {
    margin-top: 26px;
    padding-top: 26px;
    border-top: 1px solid #eeeef2;
  }
`;

const FieldLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #9c9ca8;
  margin-bottom: 6px;
`;

const FieldContact = styled.div`
  font-size: 13.5px;
  color: #444;
  margin-bottom: 14px;

  strong {
    color: #1a1a22;
    font-weight: 600;
  }
`;

const DigitRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

const DigitInput = styled.input`
  flex: 0 0 42px;
  width: 42px;
  height: 46px;
  margin: 0;
  padding: 0;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: #1a1a22;
  background: #fafafc;
  border: 1px solid #dcdce2;
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
  font-family: inherit;
  appearance: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;

  &:focus {
    background: #fff;
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.14);
  }
`;

const ErrorText = styled.div`
  margin: 20px 0 0;
  font-size: 13px;
  color: #d32f2f;
  text-align: center;
`;

const OtpVerificationDialog: React.FC<OtpVerificationDialogProps> = ({
  open,
  fields,
  codes,
  error,
  onCodeChange,
  onCancel,
  onConfirm,
}) => {
  const handleKeyDown = (
    fieldName: string,
    digitIndex: number,
    digit: string,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Backspace' && !digit && digitIndex > 0) {
      const prevInput = document.getElementById(
        `otp-${fieldName}-${digitIndex - 1}`,
      );
      prevInput?.focus();
    }
  };

  const handleChange = (
    fieldName: string,
    digitIndex: number,
    rawValue: string,
  ) => {
    const digits = rawValue.replace(/\D/g, '');
    const value = digits.slice(-1);
    onCodeChange(fieldName, digitIndex, value);
    if (value && digitIndex < 5) {
      const nextInput = document.getElementById(
        `otp-${fieldName}-${digitIndex + 1}`,
      );
      nextInput?.focus();
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onCancel}
      icon={<EnvelopeIcon />}
      title="Verify to continue"
      subtitle="Enter the 6-digit code sent to confirm your details."
      actions={[{ label: 'Confirm OTP', onClick: onConfirm }]}
    >
      {fields.map((entry) => (
        <FieldBlock key={entry.fieldName}>
          <FieldLabel>{entry.label}</FieldLabel>
          <FieldContact>
            Code sent to <strong>{entry.contact}</strong>
          </FieldContact>
          <DigitRow>
            {(codes[entry.fieldName] || Array(6).fill('')).map(
              (digit, digitIndex) => (
                <DigitInput
                  key={digitIndex}
                  id={`otp-${entry.fieldName}-${digitIndex}`}
                  value={digit}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(entry.fieldName, digitIndex, e.target.value)
                  }
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                    handleKeyDown(entry.fieldName, digitIndex, digit, e)
                  }
                />
              ),
            )}
          </DigitRow>
        </FieldBlock>
      ))}

      {error && <ErrorText>{error}</ErrorText>}
    </AppDialog>
  );
};

export default OtpVerificationDialog;
